import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  api,
  ApiError,
  askQuestion,
  contractsQueryKey,
  fetchContractRegister,
  buyerQuestionsQueryKey,
  documentSearchQueryKey,
  fetchDocumentSearch,
  hasSearchCriteria,
  listBuyerQuestions,
  listQuestions,
  questionsQueryKey,
} from '@dataroom/shared';
import type {
  RequestBuyerTeamMemberResponse,
  AskQuestionRequest,
  BuyerLoiResponse,
  ExclusivityView,
  ListDocumentsResponse,
  ListSigningEnvelopesResponse,
  MyBuyerTeamResponse,
  NewDocumentAlertPreference,
  RecordConsentRequest,
  RoomFunnelResponse,
  SearchDocumentsQuery,
  SessionResponse,
  SetTeamRolesResponse,
  SignEnvelopeRequest,
  SubmitLoiRequest,
  SubmitLoiResponse,
  TeamRole,
} from '@dataroom/shared';
import { buyerRooms } from '@/config';

/** The buyer's full position in the date-gated funnel — stage plus the dated
 * post-LOI steps (exclusivity/closing, PRD-0004 §1.1). `retry: false` so a 401
 * surfaces immediately (RoomPage redirects to /login on it). */
export function useRoomFunnel(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'funnel'],
    queryFn: () => api.get<RoomFunnelResponse>(`/rooms/${roomId}/funnel`),
    retry: false,
  });
}

/** Documents the buyer may see AT THE CURRENT STAGE — the API is the choke
 * point (teaser docs at `teaser`, CIM docs once the NDA is sealed). Each row's
 * `canDownload` says whether the buyer's grant allows downloading it; the viewer
 * offers Download only then (the content route still decides and audits). */
export function useRoomDocuments(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'documents'],
    queryFn: () =>
      api.get<ListDocumentsResponse>(`/rooms/${roomId}/documents`).then((r) => r.documents),
    retry: false,
  });
}

/** PRD-0004 §4 "find a document": free-text search across the documents the
 * buyer may see at the current funnel stage. The API is the choke point, so a
 * hit can never fall outside the stage-scoped set. Disabled until there is a
 * query, so an empty search bar leaves the stage listing in charge. */
export function useDocumentSearch(roomId: string, params: SearchDocumentsQuery) {
  return useQuery({
    queryKey: documentSearchQueryKey(roomId, params),
    queryFn: () => fetchDocumentSearch(roomId, params),
    enabled: hasSearchCriteria(params),
    retry: false,
  });
}

/** The buyer's ADMIN-ISSUED signing envelopes (NDA/NC). The buyer walks these;
 * it never creates them. */
export function useSigningEnvelopes(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'envelopes'],
    queryFn: () =>
      api
        .get<ListSigningEnvelopesResponse>(`/rooms/${roomId}/signing/envelopes`)
        .then((r) => r.envelopes),
    retry: false,
  });
}

/** Step 1 of the envelope walk: record the buyer's e-sign consent. */
export function useRecordConsent(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ envelopeId, consentText }: { envelopeId: string } & RecordConsentRequest) =>
      api.post<{ ok: boolean }>(`/rooms/${roomId}/signing/envelopes/${envelopeId}/consent`, {
        consentText,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms', roomId, 'envelopes'] }),
  });
}

/** Step 2 of the envelope walk: apply the buyer's signature — their typed full
 * name, the signature text of record (POST .../sign { signatureName }). */
export function useSignEnvelope(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ envelopeId, signatureName }: { envelopeId: string } & SignEnvelopeRequest) =>
      api.post<{ ok: boolean }>(`/rooms/${roomId}/signing/envelopes/${envelopeId}/sign`, {
        signatureName,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms', roomId, 'envelopes'] }),
  });
}

/** Step 3 of the envelope walk: seal the signed artifact. On success the funnel
 * advances and CIM unlocks, so we invalidate the funnel + documents queries too
 * (they re-fetch and the CIM documents appear). The refetches are RETURNED, so
 * the mutation stays pending — and the Seal button disabled — until the new
 * state has landed (no second click on an already-sealed envelope → raw 409). */
export function useSealEnvelope(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    // No body: the server seals and hashes the STORED document version itself
    // (it ignores any client-sent bytes), so the client builds no artifact.
    mutationFn: ({ envelopeId }: { envelopeId: string }) =>
      api.post<{ envelopeId: string; documentHash: string }>(
        `/rooms/${roomId}/signing/envelopes/${envelopeId}/seal`,
      ),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['rooms', roomId, 'envelopes'] }),
        qc.invalidateQueries({ queryKey: ['rooms', roomId, 'funnel'] }),
        qc.invalidateQueries({ queryKey: ['rooms', roomId, 'documents'] }),
        // The unlocked documents must show up in an active search as well.
        qc.invalidateQueries({ queryKey: ['rooms', roomId, 'search'] }),
      ]),
  });
}

/** Submit the letter of intent by UPLOADING THE SIGNED DOCUMENT (ADR-0029) —
 * allowed at `cim`, or after a countered / rejected round. The LOI is recorded as
 * submitted and the funnel advances to `loi_submitted` in the same request. */
export function useSubmitLoi(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SubmitLoiRequest) =>
      api.post<SubmitLoiResponse>(`/rooms/${roomId}/loi`, body),
    // RETURN the refetches: the picker stays disabled until the new LOI has
    // landed (and the card swaps to its status) — no second upload in between.
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['rooms', roomId, 'funnel'] }),
        qc.invalidateQueries({ queryKey: ['rooms', roomId, 'loi'] }),
      ]),
  });
}

/** The buyer's own latest LOI: id, status, dates and the uploaded document,
 * which the LOI card opens read-only. `retry: false` so a 401 surfaces
 * immediately. */
export function useBuyerLoi(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'loi'],
    queryFn: () => api.get<BuyerLoiResponse>(`/rooms/${roomId}/loi`).then((r) => r.loi),
    retry: false,
  });
}

/** ADR-0046 enforced exclusivity, as THIS buyer may see it: whether another
 * buyer's exclusivity pauses their access (and the countdown only when the room
 * shows it to them). Read-only — a buyer has no exclusivity controls. Refetched
 * every minute so a pause (or its end) shows without a reload; a 404 (no part
 * in the room) reads as no exclusivity. */
export function useBuyerExclusivity(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'exclusivity'],
    queryFn: () => api.get<ExclusivityView>(`/rooms/${roomId}/exclusivity`),
    retry: false,
    refetchInterval: 60_000,
  });
}

/** The buyer's OWN named team (migration 0070) — never anyone else's. A 404
 * (a buyer-run room, or no buyer access) means there is no team page here. */
export function useMyTeam(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'team'],
    queryFn: () => api.get<MyBuyerTeamResponse>(`/rooms/${roomId}/team`),
    retry: false,
  });
}

/** ASK to add a person (any email domain) to the buyer's own team — the
 * intermediary approves. Always answered 202 with the waiting request. */
export function useAddTeamMember(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, roles }: { email: string; roles?: TeamRole[] }) =>
      api.post<RequestBuyerTeamMemberResponse>(
        `/rooms/${roomId}/team`,
        roles && roles.length > 0 ? { email, roles } : { email },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms', roomId, 'team'] }),
  });
}

/** Change the roles (Legal, Financial, ...) of a person on the buyer's own team; an
 * empty list clears them. */
export function useSetTeamRoles(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, roles }: { memberId: string; roles: TeamRole[] }) =>
      api.put<SetTeamRolesResponse>(`/rooms/${roomId}/team-roles/${memberId}`, { roles }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms', roomId, 'team'] }),
  });
}

/** Withdraw (or clear a declined) ask of the buyer's own. */
export function useWithdrawTeamRequest(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) =>
      api.delete<{ ok: boolean }>(`/rooms/${roomId}/team/requests/${requestId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms', roomId, 'team'] }),
  });
}

/** Take a person off the buyer's own team (their access through the team ends). */
export function useRemoveTeamMember(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) =>
      api.delete<{ ok: boolean }>(`/rooms/${roomId}/team/${memberId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rooms', roomId, 'team'] }),
  });
}

/** The buyer's own questions (ADR-0039) — the API returns only questions this
 * buyer asked, and only answers the intermediary has released. */
export function useMyQuestions(roomId: string) {
  return useQuery({
    queryKey: questionsQueryKey(roomId),
    // The whole response: `mode` says whether this is a buyer-run room's team list.
    queryFn: () => listQuestions(roomId),
    retry: false,
  });
}

/** The shared Buyer Questions (ADR-0039) — `available` only once this buyer can see the CIM. */
export function useBuyerQuestions(roomId: string) {
  return useQuery({
    queryKey: buyerQuestionsQueryKey(roomId),
    queryFn: () => listBuyerQuestions(roomId),
    retry: false,
  });
}

/** Ask a question (ADR-0039): privately to the deal team, or — in a buyer-run room — to the seller. */
export function useAskQuestion(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AskQuestionRequest) => askQuestion(roomId, body),
    // RETURN the refetch so the form stays disabled until the question is listed.
    onSuccess: () => qc.invalidateQueries({ queryKey: questionsQueryKey(roomId) }),
  });
}

/** The signed-in actor's identity + the rooms they are a principal of
 * (GET /auth/session). The buyer portal resolves its rooms from this. `retry:
 * false` so a 401 surfaces immediately (the landing/guard redirect to /login). */
export function useSession() {
  return useQuery({
    queryKey: ['auth', 'session'],
    queryFn: () => api.get<SessionResponse>('/auth/session'),
    retry: false,
  });
}

export type BuyerRoomAccess =
  | { status: 'login' }
  | { status: 'loading' }
  | { status: 'forbidden' }
  | { status: 'ok'; roomId: string };

/**
 * Resolves a room-scoped buyer view from the `:roomId` path param, verifying it
 * is actually one of the caller's member (buyer) rooms — never trust the id in
 * the URL. Every room-scoped view funnels through this: 'login' → /login,
 * 'loading' → spinner, 'forbidden' → back to the landing.
 */
export function useBuyerRoomAccess(): BuyerRoomAccess {
  const session = useSession();
  const { roomId } = useParams<{ roomId: string }>();
  if (session.error instanceof ApiError && session.error.status === 401) return { status: 'login' };
  if (session.isPending) return { status: 'loading' };
  const rooms = buyerRooms(session.data);
  if (!roomId || !rooms.some((r) => r.roomId === roomId)) return { status: 'forbidden' };
  return { status: 'ok', roomId };
}

/**
 * GET /:roomId/contracts — the contract register (ADR-0054) over the documents
 * this buyer can already see; read-only for a buyer.
 */
export function useContractRegister(roomId: string) {
  return useQuery({
    queryKey: contractsQueryKey(roomId),
    queryFn: () => fetchContractRegister(roomId),
    retry: false,
  });
}

/** ADR-0056: the buyer's own daily new-document email for this room (on unless
 * they turned it off). `retry: false` — a non-buyer gets a 404 and no switch. */
export function useNewDocumentAlerts(roomId: string) {
  return useQuery({
    queryKey: ['rooms', roomId, 'new-document-alerts'],
    queryFn: () => api.get<NewDocumentAlertPreference>(`/rooms/${roomId}/new-document-alerts`),
    retry: false,
  });
}

/** ADR-0056: turn the new-document email on or off (the answer replaces the cached value). */
export function useSetNewDocumentAlerts(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      api.put<NewDocumentAlertPreference>(`/rooms/${roomId}/new-document-alerts`, { enabled }),
    onSuccess: (pref) => qc.setQueryData(['rooms', roomId, 'new-document-alerts'], pref),
  });
}
